<template>
  <div
    id="drill-list-view"
    class="pa-4"
  >
    <view-page-header
      v-if="attachMode === false"
      :show-team-select="false"
    />

    <div>
      <v-row
        no-gutters
        align="center"
        style="min-height: 48px"
      >
        <v-col />
        <v-col
          v-for="tag in tags"
          :key="tag.id"
          cols="auto"
          class="mr-2"
        >
          <v-select
            :value="filters.tags[tag.id]"
            :items="tag.values"
            :label="tag.label"
            solo
            style="width: 200px"
            background-color="white"
            clearable
            hide-details
            @input="handleFilterTag(tag.id, $event)"
          />
        </v-col>
        <v-col
          v-if="!hasNoPlayers"
          cols="auto"
          class="mr-2"
        >
          <players-autocomplete
            key="eventPlayersFilter"
            v-model="filters.playerId"
            :players="playersByTeam"
            :loading="players.loading"
            solo
            :chips="false"
            clearable
            hide-details
            :placeholder="$t('text_input.search_by_player.title')"
            style="width: 300px"
          />
        </v-col>
        <v-col
          v-if="!hasNoPlayers"
          cols="auto"
          class="mr-2"
        >
          <v-select
            key="eventTeamFilter"
            v-model="filters.teamId"
            :items="selectTeamItems"
            item-value="id"
            item-text="name"
            :label="$t('text_input.teams.title')"
            background-color="white"
            solo
            hide-details
            style="width: 300px"
          />
        </v-col>
        <v-col
          cols="auto"
          class="pt-1"
        >
          <v-btn
            color="primary"
            text
            class="text-none"
            @click="openDrill = true; selectedDrill = null"
          >
            <v-icon
              x-small
              left
            >
              far fa-plus
            </v-icon>
            {{ $t('button.create_new_drill') }}
          </v-btn>
        </v-col>
        <v-col cols="auto">
          <div
            v-if="drills.pagination.last_page > 1"
            class="pt-1"
          >
            <pagination
              v-model="drills.pagination.current_page"
              :last-page="drills.pagination.last_page"
            />
          </div>
        </v-col>
      </v-row>
    </div>
    <v-divider class="mt-2" />
    <div style="height: 1px">
      <v-progress-linear
        v-if="drills.loading || submitting || deleting"
        indeterminate
        color="primary"
        :height="1"
      />
    </div>

    <v-row
      v-if="drills.data.length > 0"
      class="mt-2"
    >
      <drill-card
        v-for="drill in drills.data"
        :key="drill.id"
        :drill="drill"
        @click="attachMode ? $emit('attach', drill) : selectedDrill = drill; drillInfoModal = true"
      />
    </v-row>

    <v-row
      v-else-if="!drills.loading"
      class="mt-5"
      justify="center"
    >
      <v-col cols="auto">
        {{ noFilters ? $t("drill.no_drills_created") : $t("drill.no_drills_match_filter") }}
      </v-col>
    </v-row>

    <card-dialog
      :key="'Create new' + createNewDrillKey"
      v-model="openDrill"
      :title="$t('card_title.drill.create_new')"
      :scrollable="false"
      fullscreen
      :prevent-close="true"
      @close="closeConfirmationDialogVisible = true"
    >
      <v-card-text>
        <div
          style="height: 2px"
        >
          <v-progress-linear
            v-if="submitting"
            indeterminate
            color="primary"
            :height="2"
          />
        </div>
        <HomeView
          :teams="teams"
          :players="players.data"
          :sport="user.sport.name"
          :organisation_settings="user.organisation_settings"
          :tags="tags"
          @save="handleSave"
          @saveExercise="handleSaveExercise"
          @close="openDrill = false"
        />
      </v-card-text>
    </card-dialog>

    <card-dialog
      v-if="selectedDrill"
      :key="'Edit' + editDrillKey"
      v-model="editDrill"
      :title="$t('card_title.drill.edit')"
      :scrollable="false"
      fullscreen
      :prevent-close="true"
      @close="closeConfirmationDialogVisible = true"
    >
      <v-card-text>
        <div
          style="height: 2px"
        >
          <v-progress-linear
            v-if="submitting"
            indeterminate
            color="primary"
            :height="2"
          />
        </div>
        <HomeView
          :drill="selectedDrill"
          :teams="teams"
          :players="players.data"
          :sport="user.sport.name"
          :tags="tags"
          @save="handleSave"
        />
      </v-card-text>
    </card-dialog>

    <card-dialog
      v-if="selectedDrill"
      v-model="drillInfoModal"
      :scrollable="false"
      :title="$t('card_title.drill.info')"
      max-width="1200px"
      @close="selectedDrill = null"
    >
      <v-card-text>
        <v-row
          class="mt-2"
        >
          <v-col
            cols="8"
            align-self="center"
          >
            <v-img
              v-if="selectedDrill.preview"
              id="drill_preview"
              class="rounded-xl fa-border"
              :src="selectedDrill.preview"
            />
            <v-img
              v-else
              height="450px"
              class="rounded-xl fa-border"
            >
              <template v-slot:placeholder>
                <v-row
                  no-gutters
                  align="center"
                  justify="center"
                  class="fill-height"
                >
                  <v-col cols="auto">
                    <v-icon x-large>
                      fal fa-court-sport
                    </v-icon>
                  </v-col>
                </v-row>
              </template>
            </v-img>
          </v-col>
          <v-col>
            <v-row
              class="flex-column"
              style="height: 100%;"
            >
              <v-col cols="auto">
                <v-row no-gutters>
                  <v-col>
                    <span class="text--black text-h6">
                      {{ selectedDrill.name }}
                    </span>
                  </v-col>
                  <v-col cols="auto">
                    <v-btn
                      text
                      small
                      color="primary"
                      class="text-none"
                      @click="drillInfoModal = false; editDrill = true"
                    >
                      <v-icon
                        left
                        small
                      >
                        far fa-pen
                      </v-icon>
                      {{ $t("button.edit") }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="auto">
                <span class="text--black text-subtitle-1">
                  {{ selectedDrill.description }}
                </span>
              </v-col>
              <v-spacer />
              <v-col cols="auto">
                <v-row>
                  <v-col>
                    <span
                      v-if="selectedDrill.coach"
                      class="text--black text-subtitle-1"
                    >
                      {{ $t('drill_info.dialog.made_by') }} {{ selectedDrill.coach.first_name }} {{ selectedDrill.coach.last_name }}
                    </span>
                  </v-col>
                  <v-col cols="auto">
                    <span class="text--black text-subtitle-1">
                      {{ $dayjs(selectedDrill.created_at).format('DD.MM.YYYY.') }}
                    </span>
                  </v-col>
                </v-row>
              </v-col>
              <v-col
                cols="auto"
                class="pt-0"
              >
                <v-btn
                  text
                  color="red"
                  class="text-none"
                  outlined
                  width="100%"
                  @click="deleteConfirmationDialogVisible = true"
                >
                  <v-icon
                    left
                    x-small
                  >
                    fa fa-trash
                  </v-icon>
                  {{ $t('button.delete') }}
                </v-btn>
              </v-col>
              <v-col
                cols="auto"
                class="pt-0"
              >
                <v-btn
                  text
                  color="primary"
                  class="text-none"
                  outlined
                  width="100%"
                  @click="duplicate()"
                >
                  <v-icon
                    left
                    x-small
                  >
                    fa fa-copy
                  </v-icon>
                  {{ $t("button.duplicate") }}
                </v-btn>
              </v-col>
              <v-col
                cols="auto"
                class="py-0"
              >
                <v-btn
                  color="primary"
                  class="text-none"
                  width="100%"
                  @click="editDrill = true"
                >
                  <v-icon
                    left
                    x-small
                  >
                    fa fa-file-export
                  </v-icon>
                  {{ $t("button.export") }}
                </v-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-card-text>
    </card-dialog>

    <delete-confirmation-dialog
      :visible.sync="deleteConfirmationDialogVisible"
      @confirmed="deleteDrill()"
    />

    <delete-confirmation-dialog
      :visible.sync="closeConfirmationDialogVisible"
      :title="$t('card_dialog.lose_progress.title')"
      :text="$t('card_dialog.lose_progress.label')"
      :cta-text="$t('card_dialog.lose_progress.button')"
      @confirmed="openDrill = false; editDrill = false; selectedDrill = null; closeConfirmationDialogVisible = false"
    />
  </div>
</template>

<script>
import ViewPageHeader from '@/components/ViewPageHeader'
import DrillCard from '@/components/drill/DrillCard.vue'
import { DrillApiService } from '@/data/services/DrillApiService'
import Pagination from '@/components/Pagination.vue'
import HomeView from '@ultrax-ai/drill-package'
import CardDialog from '@/components/helpers/CardDialog.vue'
import { EventBus } from '@/plugins/bus'
import { PlayerApiService } from '@/data/services'
import PlayersAutocomplete from '@/components/helpers/PlayersAutocomplete.vue'
import { TrainingExerciseApiService } from '@/data/services/TrainingExerciseApiService'
import DeleteConfirmationDialog from '@/components/dialogs/DeleteConfirmationDialog.vue'

export default {
  name: 'DrillListView',
  components: {
    DeleteConfirmationDialog,
    PlayersAutocomplete,
    CardDialog,
    Pagination,
    DrillCard,
    ViewPageHeader,
    HomeView
  },
  props: {
    attachMode: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    drills: {
      data: [],
      loading: false,
      pagination: {
        current_page: 1,
        last_page: undefined,
        per_page: 10
      }
    },
    players: {
      data: [],
      loading: false
    },
    filters: {
      teamId: 'all',
      playerId: null,
      tags: {}
    },
    groups: {
      loading: false,
      data: []
    },
    openDrill: false,
    editDrill: false,
    submitting: false,
    deleting: false,
    drillInfoModal: false,
    selectedDrill: null,
    playersForDrill: null,
    deleteConfirmationDialogVisible: false,
    closeConfirmationDialogVisible: false,
    createNewDrillKey: 0,
    editDrillKey: 0
  }),
  computed: {
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    teams() {
      return this.$store.getters[this.$storeGetters.APP_TEAMS]
    },
    playersByTeam() {
      if (!this.filters.teamId || this.filters.teamId === 'all') return this.players.data

      return this.players.data.filter(p => p.team_id === this.filters.teamId)
    },
    selectTeamItems() {
      const selectTeamItems = []

      selectTeamItems.push({
        id: 'all',
        name: this.$t('dropdown.team_selection.all_teams')
      })
      selectTeamItems.push({ divider: true })
      selectTeamItems.push(...this.teams)

      return selectTeamItems
    },
    selectedGroup() {
      // SAVE TO FOOTBALL GROUP UNTIL OTHER SPORTS AREN'T DEVELOPED
      // return this.groups.data.find(g => g.name === this.user.sport.name)
      return this.groups.data.find(g => g.name === 'Football')
    },
    tags() {
      if (this.selectedGroup) return this.selectedGroup.params.tags

      return null
    },
    noFilters() {
      return this.filters.teamId === 'all' && this.filters.playerId === null && Object.keys(this.filters.tags).length === 0
    },
    hasNoPlayers() {
      return this.user.organisation_settings?.has_no_players || false
    }
  },
  watch: {
    'drills.pagination.current_page': function (newVal, oldVal) {
      this.fetchDrills()
    },
    'filters.teamId': function () {
      const oldPlayerId = this.filters.playerId
      this.filters.playerId = null
      if (oldPlayerId === this.filters.playerId) this.fetchDrills()
    },
    'filters.playerId': function () {
      this.fetchDrills()
    },
    openDrill: function () {
      this.createNewDrillKey++
    },
    editDrill: function () {
      this.editDrillKey++
    }
  },
  created() {
    this.fetchDrills()
    this.fetchPlayers()
    this.fetchAllGroups()

    EventBus.$on(this.$events.DRILL_CREATED, () => this.fetchDrills())
    EventBus.$on(this.$events.DRILL_UPDATED, () => this.fetchDrills())
    EventBus.$on(this.$events.DRILL_DELETED, () => this.fetchDrills())

    document.addEventListener('keydown', this.handleKeyboard.bind(this))
  },
  destroyed() {
    EventBus.$off(this.$events.DRILL_CREATED)
    EventBus.$off(this.$events.DRILL_UPDATED)
    EventBus.$off(this.$events.DRILL_DELETED)

    document.removeEventListener('keydown', this.handleKeyboard.bind(this))
  },
  methods: {
    async fetchDrills() {
      try {
        this.drills.loading = true

        const queryParams = {
          page: this.drills.pagination.current_page,
          per_page: this.drills.pagination.per_page,
          team_id: this.filters.teamId !== 'all' ? this.filters.teamId : undefined,
          player_id: this.filters.playerId || undefined,
          tags: this.filters.tags
        }

        const result = await DrillApiService.getPaginated(queryParams)

        this.drills.data = result.data
        this.drills.pagination = result.meta

        if (this.jumpToLastPage) {
          this.drills.pagination.current_page = this.drills.pagination.last_page
          this.jumpToLastPage = false
        }
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.drill_loading.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.drills.loading = false
      }
    },
    async handleSave(saveData) {
      if (saveData.state) {
        await this.save(saveData)
      }
    },
    async handleSaveExercise(saveData) {
      try {
        this.submitting = true

        const drillData = {
          name: saveData.name,
          description: saveData.description,
          preview: saveData.preview,
          state: saveData.state,
          coach_id: this.user.coach.id,
          team_ids: saveData.team_ids,
          player_ids: saveData.player_ids,
          is_hidden: true,
          tags: saveData.tags
        }

        const result = await DrillApiService.create(drillData)

        const exerciseData = {
          name: saveData.name,
          description: saveData.description,
          training_exercise_group_id: this.selectedGroup.id,
          tags: saveData.tags,
          media_ids: [],
          drill_id: result.data.id
        }

        await TrainingExerciseApiService.createUserExercise(exerciseData)

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.exercise_save.success'),
          color: 'success'
        })

        this.openDrill = false

        EventBus.$emit(this.$events.TRAINING_EXERCISE_CREATED, result.data)
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.exercise_save.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.submitting = false
      }
    },
    async save(saveData) {
      try {
        this.submitting = true

        const data = {
          name: saveData.name,
          description: saveData.description,
          preview: saveData.preview,
          state: saveData.state,
          coach_id: this.user.coach.id,
          team_ids: saveData.team_ids,
          player_ids: saveData.player_ids,
          tags: saveData.tags
        }

        if (this.selectedDrill) {
          await DrillApiService.update(this.selectedDrill.id, data)
        } else {
          await DrillApiService.create(data)
        }

        if (this.selectedDrill) {
          this.$store.dispatch(this.$storeActions.ALERT, {
            text: this.$t('alert.drill_update.success'),
            color: 'success'
          })

          EventBus.$emit(this.$events.DRILL_UPDATED)
        } else {
          this.$store.dispatch(this.$storeActions.ALERT, {
            text: this.$t('alert.drill_save.success'),
            color: 'success'
          })

          EventBus.$emit(this.$events.DRILL_CREATED)
        }
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.drill_save.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.submitting = false
      }
    },
    async deleteDrill() {
      try {
        this.deleting = true
        this.deleteConfirmationDialogVisible = false

        await DrillApiService.delete(this.selectedDrill.id)

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.drill_delete.success'),
          color: 'success'
        })

        EventBus.$emit(this.$events.DRILL_DELETED)
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.drill_delete.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.deleting = false
        this.selectedDrill = null
      }
    },
    async fetchPlayers() {
      try {
        this.players.loading = true

        const resp = await PlayerApiService.getSimpleList({})

        this.players.data = resp.data
      } catch (e) {
        this.players.data = []
      } finally {
        this.players.loading = false
      }
    },
    async fetchAllGroups() {
      this.groups.loading = true

      try {
        const result = await TrainingExerciseApiService.getAllGroups()

        this.groups.data = result
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_groups.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.groups.loading = false
      }
    },
    handleFilterTag(tagId, tagValue) {
      if (!this.filters.tags[tagId] && !tagValue) return

      this.filters.tags[tagId] = !tagValue ? undefined : tagValue

      if (this.drills.pagination.current_page !== 1) {
        this.drills.pagination.current_page = 1
      } else {
        this.fetchDrills()
      }
    },
    duplicate() {
      const saveData = {
        name: this.selectedDrill.name + ' copy',
        description: this.selectedDrill.description,
        preview: this.selectedDrill.preview,
        state: this.selectedDrill.state,
        coach_id: this.user.coach.id,
        team_ids: this.selectedDrill.team_ids,
        player_ids: this.selectedDrill.player_ids,
        tags: this.selectedDrill.tags.reduce((acc, tag) => {
          acc[tag.tag_id] = tag.value
          return acc
        }, {})
      }
      this.selectedDrill = null

      this.jumpToLastPage = true

      this.save(saveData)
    },
    handleKeyboard(event) {
      event.stopPropagation()
      if (event.keyCode === 27) {
        if (this.openDrill || this.editDrill) this.closeConfirmationDialogVisible = true
      }
    }
  }

}
</script>
